import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RedirectIfAuthenticated } from './features/auth/RequireAuth';
import { RequirePermission } from './features/auth/RequirePermission';
import { PlatformOnly, TenantOnly } from './features/auth/AreaGates';
import { LoginPage } from './features/auth/LoginPage';
import { MfaChallengePage } from './features/auth/MfaChallengePage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { AcceptInvitationPage } from './features/auth/AcceptInvitationPage';
import { NotFoundPage } from './app/pages';
import { Spinner } from './components/ui/misc';

// Route-level code-splitting — the shell, auth and gates stay in the main
// bundle; every feature page is its own chunk.
const named = <M, K extends keyof M>(loader: () => Promise<M>, key: K) =>
  lazy(() => loader().then((m) => ({ default: m[key] as ComponentType })));

const DashboardPage = named(() => import('./features/dashboard/DashboardPage'), 'DashboardPage');
const EmployeesListPage = named(() => import('./features/employees/EmployeesListPage'), 'EmployeesListPage');
const EmployeeDetailPage = named(() => import('./features/employees/EmployeeDetailPage'), 'EmployeeDetailPage');
const AttendancePage = named(() => import('./features/attendance/AttendancePage'), 'AttendancePage');
const LeavePage = named(() => import('./features/leave/LeavePage'), 'LeavePage');
const VisitsPage = named(() => import('./features/access/VisitsPage'), 'VisitsPage');
const NotificationsPage = named(() => import('./features/shell/NotificationsPage'), 'NotificationsPage');
const MessagesPage = named(() => import('./features/messaging/MessagesPage'), 'MessagesPage');
const ApprovalsPage = named(() => import('./features/approvals/ApprovalsPage'), 'ApprovalsPage');
const PayrollRunsPage = named(() => import('./features/payroll/PayrollRunsPage'), 'PayrollRunsPage');
const PayrollRunDetailPage = named(() => import('./features/payroll/PayrollRunDetailPage'), 'PayrollRunDetailPage');
const DevicesListPage = named(() => import('./features/devices/DevicesListPage'), 'DevicesListPage');
const DeviceDetailPage = named(() => import('./features/devices/DeviceDetailPage'), 'DeviceDetailPage');
const ReportsPage = named(() => import('./features/reports/ReportsPage'), 'ReportsPage');
const SettingsPage = named(() => import('./features/settings/SettingsPage'), 'SettingsPage');

// Stage 4 Phase 0 exit criterion 1 — dev-only component-library preview,
// never registered outside a dev build.
const ComponentLibraryPage = import.meta.env.DEV
  ? named(() => import('./dev/ComponentLibraryPage'), 'ComponentLibraryPage')
  : null;

const PlatformHome = named(() => import('./features/platform/pages'), 'PlatformHome');
const TenantLookupPage = named(() => import('./features/platform/pages'), 'TenantLookupPage');
const OnboardTenantPage = named(() => import('./features/platform/pages'), 'OnboardTenantPage');
const TenantWorkspace = named(() => import('./features/platform/TenantWorkspace'), 'TenantWorkspace');
const StatutoryRatesPage = named(() => import('./features/platform/StatutoryRatesPage'), 'StatutoryRatesPage');
const DeviceCodesPage = named(() => import('./features/platform/DeviceCodesPage'), 'DeviceCodesPage');
const BackupStatusPage = named(() => import('./features/platform/BackupStatusPage'), 'BackupStatusPage');

function PageFallback() {
  return (
    <div className="p-6">
      <Spinner label="Loading…" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {import.meta.env.DEV && ComponentLibraryPage && (
          <Route path="/__dev/components" element={<ComponentLibraryPage />} />
        )}
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/login/mfa"
          element={
            <RedirectIfAuthenticated>
              <MfaChallengePage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RedirectIfAuthenticated>
              <ForgotPasswordPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/reset-password"
          element={
            <RedirectIfAuthenticated>
              <ResetPasswordPage />
            </RedirectIfAuthenticated>
          }
        />

        {/* Re-design T22 — an invitation's one-time link; public, and works whether or not someone is signed in here. */}
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

        <Route element={<RequireAuth />}>
          {/* Tenant portal */}
          <Route element={<TenantOnly />}>
            <Route index element={<DashboardPage />} />

            <Route element={<RequirePermission anyOf={['employee.view']} />}>
              <Route path="employees" element={<EmployeesListPage />} />
              <Route path="employees/:employeeId" element={<EmployeeDetailPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['attendance.view']} />}>
              <Route path="attendance" element={<AttendancePage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['leave.view', 'leave.approve']} />}>
              <Route path="leave" element={<LeavePage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['overtime.request', 'overtime.view', 'overtime.approve', 'leave.approve']} />}>
              <Route path="approvals" element={<ApprovalsPage />} />
            </Route>
            <Route path="notifications" element={<NotificationsPage />} />
            <Route element={<RequirePermission anyOf={['message.view']} />}>
              <Route path="messages" element={<MessagesPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['access.view']} />}>
              <Route path="visitors" element={<VisitsPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['device.view']} />}>
              <Route path="devices" element={<DevicesListPage />} />
              <Route path="devices/:deviceId" element={<DeviceDetailPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['report.view']} />}>
              <Route path="reports" element={<ReportsPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['tenant.edit']} />}>
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['payroll.view']} />}>
              <Route path="payroll" element={<PayrollRunsPage />} />
              <Route path="payroll/:runId" element={<PayrollRunDetailPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Platform console */}
          <Route path="platform" element={<PlatformOnly />}>
            <Route index element={<PlatformHome />} />
            <Route element={<RequirePermission anyOf={['tenant.view']} />}>
              <Route path="lookup" element={<TenantLookupPage />} />
              <Route path="tenant" element={<TenantWorkspace />} />
            </Route>
            <Route element={<RequirePermission anyOf={['tenant.create']} />}>
              <Route path="onboard" element={<OnboardTenantPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['audit.view']} />}>
              <Route path="statutory-rates" element={<StatutoryRatesPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['device_mappings.read']} />}>
              <Route path="device-codes" element={<DeviceCodesPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={['platform.backup.view']} />}>
              <Route path="backups" element={<BackupStatusPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
